import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { AlertTriangle, CheckCircle, FileText, Info } from 'lucide-react';
import moment from 'moment';

const VatVerificationModal = ({ showModal, setShowModal, transactions, totalDebit, totalCredit, verifying, error, handleConfirmVerification, vatPeriod, modalType = 'review', verificationDescription }) => {
    const formatCurrency = (amount) => {
        const num = Math.abs(parseFloat(amount) || 0);
        return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(num);
    };

    const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

    const modalTitle = modalType === 'record' ? 'Dragning Skatteverket' : 'Momsredovisning';
    const modalInfo = modalType === 'record'
        ? 'Verifikatet bokför dragningen från skattekontot när Skatteverket drar pengarna.'
        : 'Verifikatet nollställer alla momskonton för perioden och flyttar saldot till företagets momsredovisningskonto.';

    return (
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="h5">
                    <div className="d-flex align-items-center">
                        <FileText className="me-2" size={20} style={{ color: 'var(--primary)' }} />
                        {modalTitle}
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <style>{`
                    .vat-ver-info {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                        gap: 0.75rem;
                        margin-bottom: 1rem;
                    }
                    .vat-ver-info-item {
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        padding: 0.6rem 0.75rem;
                    }
                    .vat-ver-info-label {
                        font-size: 0.68rem;
                        color: var(--secondary);
                        text-transform: uppercase;
                        letter-spacing: 0.03em;
                        font-weight: 600;
                        margin-bottom: 0.15rem;
                    }
                    .vat-ver-info-value {
                        font-size: 0.85rem;
                        font-weight: 500;
                        color: #1e293b;
                    }
                    .vat-ver-table {
                        background: white;
                        border-radius: 6px;
                        overflow: hidden;
                        border: 1px solid #e2e8f0;
                    }
                    .vat-ver-table-header {
                        background: #f8fafc;
                        border-bottom: 1px solid #e2e8f0;
                        padding: 0.5rem 0.75rem;
                        font-size: 0.72rem;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.03em;
                        color: var(--secondary);
                    }
                    .vat-ver-table-row {
                        padding: 0.5rem 0.75rem;
                        border-bottom: 1px solid #f1f5f9;
                        font-size: 0.85rem;
                    }
                    .vat-ver-table-row:last-child { border-bottom: none; }
                    .vat-ver-table-row:hover { background: #fafbfc; }
                    .vat-ver-table-footer {
                        background: #f1f5f9;
                        padding: 0.5rem 0.75rem;
                        font-weight: 600;
                        font-size: 0.85rem;
                        border-top: 1px solid #e2e8f0;
                    }
                    .vat-ver-amount {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', Consolas, monospace;
                        font-size: 0.8rem;
                    }
                `}</style>

                {/* Info cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div className="vat-ver-info-item">
                        <div className="vat-ver-info-label">Beskrivning</div>
                        <div className="vat-ver-info-value">
                            {verificationDescription || modalTitle}
                        </div>
                    </div>
                    <div className="vat-ver-info-item">
                        <div className="vat-ver-info-label">Datum</div>
                        <div className="vat-ver-info-value d-flex justify-content-between align-items-center">
                            <span>{vatPeriod ? moment(vatPeriod.end_date).format('D MMM YYYY') : '—'}</span>
                            <span className="d-inline-flex align-items-center gap-1" style={{ color: isBalanced ? 'var(--primary-dark)' : 'var(--bs-danger)', fontSize: '0.78rem' }}>
                                {isBalanced ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                                {isBalanced ? 'Balanserad' : 'Obalanserad'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Info message */}
                <div className="d-flex align-items-start gap-2 mb-3 px-1" style={{ fontSize: '0.8rem', color: 'var(--secondary)' }}>
                    <Info size={14} className="mt-1 flex-shrink-0" />
                    <span>{modalInfo}</span>
                </div>

                {verifying && (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2 small">Skapar verifikat...</p>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.8rem' }}>
                        <AlertTriangle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Transaction table */}
                <div className="vat-ver-table">
                    <div className="vat-ver-table-header d-flex">
                        <div style={{ flex: 1, minWidth: '50px' }}>Konto</div>
                        <div style={{ flex: 3 }}>Kontonamn</div>
                        <div style={{ flex: 1, textAlign: 'right' }}>Debet</div>
                        <div style={{ flex: 1, textAlign: 'right' }}>Kredit</div>
                    </div>

                    {transactions.map((transaction, index) => {
                        const debitAmt = parseFloat(transaction.debit || 0);
                        const creditAmt = parseFloat(transaction.credit || 0);
                        const hasDebit = debitAmt !== 0;
                        const hasCredit = creditAmt !== 0;

                        return (
                            <div key={index} className="vat-ver-table-row d-flex align-items-center">
                                <div style={{ flex: 1, minWidth: '50px' }}>
                                    <span className="fw-medium" style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                                        {transaction.account_number}
                                    </span>
                                </div>
                                <div style={{ flex: 3, color: '#475569', fontSize: '0.82rem' }}>
                                    {transaction.account_name}
                                </div>
                                <div style={{ flex: 1, textAlign: 'right' }} className="vat-ver-amount">
                                    {hasDebit ? (
                                        <span className="fw-medium" style={{ color: 'var(--primary-dark)' }}>{formatCurrency(debitAmt)}</span>
                                    ) : (
                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                    )}
                                </div>
                                <div style={{ flex: 1, textAlign: 'right' }} className="vat-ver-amount">
                                    {hasCredit ? (
                                        <span className="fw-medium" style={{ color: 'var(--primary)' }}>{formatCurrency(creditAmt)}</span>
                                    ) : (
                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div className="vat-ver-table-footer d-flex">
                        <div style={{ flex: 1, minWidth: '50px' }}></div>
                        <div style={{ flex: 3 }}>Summa</div>
                        <div className="vat-ver-amount" style={{ flex: 1, textAlign: 'right', color: 'var(--primary-dark)' }}>
                            {formatCurrency(totalDebit)}
                        </div>
                        <div className="vat-ver-amount" style={{ flex: 1, textAlign: 'right', color: 'var(--primary)' }}>
                            {formatCurrency(totalCredit)}
                        </div>
                    </div>
                </div>

                {!isBalanced && (
                    <Alert variant="warning" className="mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                        <AlertTriangle size={14} className="me-2" />
                        Transaktioner är inte balanserade. Kontrollera beloppen.
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleConfirmVerification}
                    disabled={verifying || !isBalanced}
                >
                    {verifying ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Skapar...
                        </>
                    ) : (
                        'Bekräfta och skapa verifikat'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VatVerificationModal;
